/** Before / after highlighting for the validation drawer: which parts of a stored value the
 *  normaliser removed and which it added. Pure functions; nothing here touches the value. */

/** One run of a value, flagged when the other side does not have it. */
export interface DiffSegment {
  text: string;
  changed: boolean;
}

/** Whether a character is one the normaliser removes that would otherwise be invisible: a C0
 *  control other than tab / line feed / carriage return, DEL, or a zero-width character. */
function isInvisible(code: number): boolean {
  return (
    (code < 0x20 && code !== 0x09 && code !== 0x0a && code !== 0x0d) ||
    code === 0x7f ||
    isZeroWidth(code)
  );
}

function isZeroWidth(code: number): boolean {
  return (code >= 0x200b && code <= 0x200d) || code === 0xfeff;
}

/** The tokens a text is compared by: each invisible character alone, whitespace runs, words. */
function tokenize(text: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let kind: 'space' | 'word' | null = null;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (isInvisible(code)) {
      if (current) tokens.push(current);
      tokens.push(char);
      current = '';
      kind = null;
      continue;
    }
    const next = /\s/.test(char) ? 'space' : 'word';
    if (kind !== null && next !== kind) {
      tokens.push(current);
      current = '';
    }
    current += char;
    kind = next;
  }
  if (current) tokens.push(current);
  return tokens;
}

/** Edit distance past which two values are treated as wholly different (a mirrored or rewritten
 *  field): the comparison stays cheap, and a whole-value highlight is the honest answer. */
const MAX_EDITS = 400;

/** Which items of `a` and of `b` are outside their longest common subsequence (Myers' O(ND)
 *  diff). `null` when more than {@link MAX_EDITS} edits apart. */
export function diffKeep<T>(
  a: readonly T[],
  b: readonly T[],
  equal: (x: T, y: T) => boolean = Object.is,
): { a: boolean[]; b: boolean[] } | null {
  const n = a.length;
  const m = b.length;
  const offset = n + m + 1;
  const v = new Array<number>(2 * offset + 1).fill(0);
  const trace: number[][] = [];
  for (let d = 0; d <= Math.min(n + m, MAX_EDITS); d++) {
    trace.push(v.slice());
    for (let k = -d; k <= d; k += 2) {
      const down = k === -d || (k !== d && (v[offset + k - 1] ?? 0) < (v[offset + k + 1] ?? 0));
      let x = down ? (v[offset + k + 1] ?? 0) : (v[offset + k - 1] ?? 0) + 1;
      let y = x - k;
      while (x < n && y < m && equal(a[x] as T, b[y] as T)) {
        x++;
        y++;
      }
      v[offset + k] = x;
      if (x >= n && y >= m) return backtrack(trace, offset, n, m, d);
    }
  }
  return null;
}

function backtrack(
  trace: number[][],
  offset: number,
  n: number,
  m: number,
  depth: number,
): { a: boolean[]; b: boolean[] } {
  const changedA = new Array<boolean>(n).fill(false);
  const changedB = new Array<boolean>(m).fill(false);
  let x = n;
  let y = m;
  for (let d = depth; d > 0; d--) {
    const v = trace[d] ?? [];
    const k = x - y;
    const down = k === -d || (k !== d && (v[offset + k - 1] ?? 0) < (v[offset + k + 1] ?? 0));
    const prevK = down ? k + 1 : k - 1;
    const prevX = v[offset + prevK] ?? 0;
    const prevY = prevX - prevK;
    while (x > prevX && y > prevY) {
      x--;
      y--;
    }
    if (down) changedB[prevY] = true;
    else changedA[prevX] = true;
    x = prevX;
    y = prevY;
  }
  return { a: changedA, b: changedB };
}

/** Consecutive tokens with the same flag merged into one segment. */
function merge(tokens: readonly string[], changed: readonly boolean[]): DiffSegment[] {
  const out: DiffSegment[] = [];
  tokens.forEach((text, index) => {
    const flag = changed[index] ?? false;
    const last = out[out.length - 1];
    if (last?.changed === flag) last.text += text;
    else out.push({ text, changed: flag });
  });
  return out;
}

/** `before` and `after` split into segments, the parts each side alone has flagged. Too far
 *  apart: each side is one changed segment. */
export function diffText(
  before: string,
  after: string,
): { before: DiffSegment[]; after: DiffSegment[] } {
  if (before === after) {
    return { before: [{ text: before, changed: false }], after: [{ text: after, changed: false }] };
  }
  // A side that is only whitespace was replaced, not edited: matching its spaces against the
  // other side's would claim nothing was removed.
  if (before.trim() === '' || after.trim() === '') {
    return { before: [{ text: before, changed: true }], after: [{ text: after, changed: true }] };
  }
  const a = tokenize(before);
  const b = tokenize(after);
  const keep = diffKeep(a, b);
  if (!keep) {
    return { before: [{ text: before, changed: true }], after: [{ text: after, changed: true }] };
  }
  return { before: merge(a, keep.a), after: merge(b, keep.b) };
}

/** One character of a changed run made visible: a control as ␛, a zero-width character as ◌,
 *  a tab as ⇥, a space or no-break space as ·, a carriage return as ␍. Display only. */
function visibleChar(char: string): string {
  if (char === '\t') return '⇥';
  if (char === ' ' || char === '\u00a0') return '·';
  if (char === '\r') return '␍';
  const code = char.codePointAt(0) ?? 0;
  if (isZeroWidth(code)) return '◌';
  if (isInvisible(code)) return '␛';
  return char;
}

/** A changed run made visible. Display only.
 *
 *  A run of only whitespace / invisible characters (the normaliser collapsed a double space,
 *  dropped a tab or a \r) shows each one as its marker — a space as ·, a line break as ↵ (kept) —
 *  or the change would be invisible. A run with words in it (a rewritten sentence) keeps its
 *  spaces, tabs and line breaks as they are, so it reads and wraps as text; only characters
 *  that would otherwise not show at all (controls, zero-width, \r) are marked inside it. */
export function makeVisible(text: string): string {
  const chars = Array.from(text);
  if (chars.some((char) => !isBlank(char))) {
    return chars.map((char) => (isMarkedInText(char) ? visibleChar(char) : char)).join('');
  }
  return chars.map((char) => (char === '\n' ? '↵\n' : visibleChar(char))).join('');
}

/** Whitespace or a character that renders as nothing. */
function isBlank(char: string): boolean {
  return /\s/.test(char) || isInvisible(char.codePointAt(0) ?? 0);
}

/** Inside a run with words, only what would otherwise not show at all gets a marker. */
function isMarkedInText(char: string): boolean {
  return char === '\r' || isInvisible(char.codePointAt(0) ?? 0);
}

/** One line of a compared value (1-based), and whether anything on it changed. */
export interface DiffLine {
  number: number;
  segments: DiffSegment[];
  changed: boolean;
}

/** `segments` split into lines, for the changes-only view. A removed or added line break (a
 *  whitespace-only change) counts as a change on the line it ends, shown as ↵ there. */
export function toLines(segments: readonly DiffSegment[]): DiffLine[] {
  let line: DiffLine = { number: 1, segments: [], changed: false };
  const lines: DiffLine[] = [line];
  for (const segment of segments) {
    const pieces = segment.text.split('\n');
    // A line break is itself the change only in a whitespace-only run; inside a rewritten
    // sentence it is just where the text wraps.
    const breakIsChange = segment.changed && Array.from(segment.text).every(isBlank);
    pieces.forEach((piece, index) => {
      if (piece) {
        line.segments.push({ text: piece, changed: segment.changed });
        if (segment.changed) line.changed = true;
      }
      if (index < pieces.length - 1) {
        if (breakIsChange) {
          line.segments.push({ text: '↵', changed: true });
          line.changed = true;
        }
        line = { number: line.number + 1, segments: [], changed: false };
        lines.push(line);
      }
    });
  }
  return lines;
}

/** For entries compared with {@link diffKeep}: which entry of the other side each changed entry
 *  replaced — the changed entries between the same two unchanged ones, paired in order. Lets a
 *  changed item or row be compared word by word with its counterpart, so a whitespace-only
 *  edit inside it shows. Unpaired (only added / only removed): absent from the map. */
export function pairChanged(
  changedA: readonly boolean[],
  changedB: readonly boolean[],
): { aToB: Map<number, number>; bToA: Map<number, number> } {
  const aToB = new Map<number, number>();
  const bToA = new Map<number, number>();
  let gapA: number[] = [];
  let gapB: number[] = [];
  const flush = () => {
    gapA.forEach((index, k) => {
      const other = gapB[k];
      if (other === undefined) return;
      aToB.set(index, other);
      bToA.set(other, index);
    });
    gapA = [];
    gapB = [];
  };
  let i = 0;
  let j = 0;
  while (i < changedA.length || j < changedB.length) {
    if (i < changedA.length && changedA[i]) gapA.push(i++);
    else if (j < changedB.length && changedB[j]) gapB.push(j++);
    else {
      // Both at an unchanged entry: they match each other, and close the gap before them.
      flush();
      i++;
      j++;
    }
  }
  flush();
  return { aToB, bToA };
}
